import React, { useEffect, useState } from "react";
import { api, formatINR } from "../lib/api";
import { toast } from "sonner";
import { Percent, X } from "lucide-react";

/**
 * Bulk discount editor — set sale prices on many products at once.
 * Used on the Admin page.
 */
export default function BulkDiscount() {
  const [products, setProducts] = useState([]);
  const [edits, setEdits] = useState({});  // { id: {price, compare_price} }
  const [percent, setPercent] = useState(20);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data));
  useEffect(() => { if (open) load(); }, [open]);

  const applyPercent = () => {
    const pct = Math.max(0, Math.min(90, Number(percent) || 0));
    const next = {};
    for (const p of products) {
      const base = p.compare_price || p.price;
      const newPrice = Math.round(base * (1 - pct / 100));
      next[p.id] = { price: newPrice, compare_price: base };
    }
    setEdits(next);
    toast(`Previewing ${pct}% off across ${products.length} products. Review and Save.`);
  };

  const clearAll = () => {
    const next = {};
    for (const p of products) {
      // restore to compare_price as base if exists, else keep current price, no discount
      next[p.id] = { price: p.compare_price || p.price, compare_price: null };
    }
    setEdits(next);
    toast("Previewing discount removal across all products. Review and Save.");
  };

  const updateRow = (id, key, val) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val === "" ? null : Number(val) } }));
  };

  const save = async () => {
    const updates = Object.entries(edits)
      .filter(([, v]) => v && v.price != null)
      .map(([product_id, v]) => ({ product_id, price: v.price, compare_price: v.compare_price ?? null }));
    if (updates.length === 0) { toast.error("Nothing to save. Apply % first or edit a row."); return; }
    setSaving(true);
    try {
      const r = await api.post("/products/bulk-discount", { updates });
      toast.success(`Updated ${r.data.updated} product(s)`);
      setEdits({});
      load();
    } catch (e) {
      toast.error("Bulk update failed");
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button data-testid="bulk-discount-open" onClick={() => setOpen(true)} className="btn-ghost text-xs py-2 px-4">
        <Percent size={14} /> Bulk Discount
      </button>
    );
  }

  return (
    <div data-testid="bulk-discount-panel" className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-[#0E0E0E] border border-white/10 w-full max-w-5xl max-h-[88vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="overline">Admin · Pricing</div>
            <h2 className="font-display text-2xl uppercase tracking-tight">Bulk Discount Editor</h2>
          </div>
          <button onClick={() => setOpen(false)} data-testid="bulk-discount-close" className="text-white/60 hover:text-white"><X /></button>
        </div>

        <div className="p-5 border-b border-white/10 flex flex-wrap items-end gap-3">
          <div className="flex items-end gap-3">
            <div>
              <div className="overline mb-1.5">Apply % Off</div>
              <input
                data-testid="bulk-percent"
                type="number" min="0" max="90"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                className="bg-[#111] border border-white/15 px-3 py-2 w-24 focus:outline-none focus:border-[#D96A1B]"
              />
            </div>
            <button data-testid="bulk-apply-percent" onClick={applyPercent} className="btn-orange py-2 px-4 text-xs">Preview Across All</button>
            <button data-testid="bulk-clear" onClick={clearAll} className="btn-ghost py-2 px-4 text-xs">Remove All Discounts</button>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-white/50 font-body">{Object.keys(edits).length} pending change(s)</span>
            <button data-testid="bulk-save" onClick={save} disabled={saving} className="btn-orange py-2 px-4 text-xs">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0E0E0E] border-b border-white/10">
              <tr className="text-xs text-white/50 font-display tracking-widest uppercase">
                <th className="text-left p-3">Product</th>
                <th className="text-right p-3">Current</th>
                <th className="text-right p-3">New Price</th>
                <th className="text-right p-3">Compare</th>
                <th className="text-right p-3">% Off</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const e = edits[p.id] || {};
                const newPrice = e.price ?? p.price;
                const compare = e.compare_price ?? p.compare_price;
                const pct = compare && newPrice < compare ? Math.round(((compare - newPrice) / compare) * 100) : 0;
                return (
                  <tr key={p.id} data-testid={`bulk-row-${p.slug}`} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <img src={p.images?.[0]} alt="" className="w-9 h-11 object-cover" />
                        <div>
                          <div className="font-display uppercase tracking-tight text-xs">{p.name}</div>
                          <div className="text-[10px] text-white/40">{p.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-right text-white/60 font-body">{formatINR(p.price)}</td>
                    <td className="p-3 text-right">
                      <input
                        data-testid={`bulk-price-${p.slug}`}
                        type="number" min="0"
                        value={e.price ?? p.price}
                        onChange={(ev) => updateRow(p.id, "price", ev.target.value)}
                        className="bg-[#111] border border-white/15 px-2 py-1.5 w-24 text-right focus:outline-none focus:border-[#D96A1B]"
                      />
                    </td>
                    <td className="p-3 text-right">
                      <input
                        type="number" min="0"
                        value={e.compare_price ?? p.compare_price ?? ""}
                        placeholder="—"
                        onChange={(ev) => updateRow(p.id, "compare_price", ev.target.value)}
                        className="bg-[#111] border border-white/15 px-2 py-1.5 w-24 text-right focus:outline-none focus:border-[#D96A1B]"
                      />
                    </td>
                    <td className="p-3 text-right font-display text-[#D96A1B]">{pct ? `-${pct}%` : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
